/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { Fragment, use, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import { useFeatureFlag } from '@/features/globalConfig';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import { cartConstants } from '@/constants/cart';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { Button } from '../Button';
import type { FulfillmentGroup, FulfillmentOption } from '@/types/reduxStore/skuDetails';
import { SubGroupText } from './SubgroupText';
import { useLabels } from '@/hooks/useLabels';
import StorePickupTime from './StorePickupTime';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import {
  type FlyoutDataState,
  SisterStoreContext,
} from '@/features/fulfillment/context/SisterStoreContext';
import dayjs from '@/lib/dayjs';
import { dateFormatterForSTH } from '@/constants/date';
import { Text } from '@az/starc-ui';
import { SearchStoreModal } from '../Store/StoreModal/SearchStoreModal';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useDeviceDetector } from '@/hooks/useDeviceDetector';
import {
  checkIfDateIsTomorrow,
  generateGetItTimePhrase,
  getTomorrowOrDate,
} from '@/features/shelf/utils/checkIfDateIsTomorrow';
import { createFreeGroundDateRange } from '@/features/shelf/utils/createFreeGroundDateRange';
import { type ShippingType } from '@/features/fulfillment';
import { determineOptionsToDisplayButtons } from '@/features/fulfillment/api/optionsToDisplay';
import usePrevious from '@/utils/usePrevious';

export type FulfillmentOptionButtonsV2Props = {
  itemData?: ItemData;
  fulfillmentOptionsGroupList: Array<FulfillmentGroup>;
  changeShippingType: (
    shippingType: typeof cartConstants.STOREORDER | typeof cartConstants.ONLINEORDER,
    groupId: typeof FULFILLMENT_METHODS.STOREORDER | typeof FULFILLMENT_METHODS.ONLINEORDER,
    maxAvailableQty: number,
    sisterStoreAvailable: boolean,
    fulfillmentTypeId?: number,
    fulfillmentTypeIdOriginal?: number,
  ) => void;
  shippingType: ShippingType;
  skuId: string | undefined;
  productName: string;
  sisterStoreAvailable?: boolean;
  handleInstockAnalytics?: () => any;
  isDealDrawer?: boolean;
  itemPresentInCart?: boolean;
  quantityInCart: number;
  price?: number;
  isCart?: boolean;
  count?: number;
  fulfillmentId: number;
  sddQuantity?: number;
  nddQuantity?: number;
  disableRemoveButton?: boolean;
  productId?: string;
};
type ItemData = {
  shipToHome: boolean;
  itemQuantity: number;
};

const labelMap = {
  label_next_day: 'label_fulfilment_options_next_day',
  label_same_Day: 'label_fulfilment_options_same_day',
  label_get_it: 'label_fulfilment_options_get_it',
  label_tomorrow: 'label_fulfilment_options_tomorrow',
  label_today: 'label_fulfilment_options_today',
  label_sdd_shipping_price: 'label_fulfilment_options_sdd_shipping_price',
  label_free: 'label_fulfilment_options_free',
  label_free_over_35: 'label_fulfillment_options_free_over_35_v2',
  label_home_delivery: 'label_fulfilment_options_home_delivery',
  label_not_available: 'label_fulfilment_options_not_available',
  label_not_available_near_you: 'label_fulfilment_options_not_available_near_you',
  label_ready: 'label_fulfilment_options_ready',
  label_available_near_you: 'label_fulfilment_options_available_near_you',
  label_instore_pickup: 'label_fulfilment_options_instore_pickup',
  label_get_it_by: 'label_fulfilment_options_get_it_by',
  label_fulfillment_options_over_35: 'label_fulfillment_options_over_35',
  label_fulfillment_select_store: 'label_fulfillment_select_store',
  label_fulfillment_availability: 'label_fulfillment_availability',
  label_shipping_express_delivery: 'label_shipping_express_delivery',
  label_shipping_ground_delivery: 'label_shipping_ground_delivery',
  label_fulfilment_options_get_it_by: 'label_fulfilment_options_get_it_by',
  label_fulfilment_options_get_it: 'label_fulfilment_options_get_it',
};

export function FulfillmentOptionButtonsV2({
  changeShippingType,
  fulfillmentOptionsGroupList,
  productName,
  shippingType,
  skuId,
  itemData,
  handleInstockAnalytics,
  isDealDrawer,
  itemPresentInCart,
  isCart = false,
  quantityInCart,
  count = 0,
  fulfillmentId,
  sddQuantity,
  nddQuantity,
  disableRemoveButton,
  productId,
}: FulfillmentOptionButtonsV2Props) {
  const labels = useLabels(labelMap);
  const { platform } = useDeviceDetector();
  const subGroupRefs = fulfillmentOptionsGroupList?.map(() => ({ ref: React.createRef<any>() }));
  const isBopusNewMsgApplicable = useFeatureFlag('IS_BOPUS_MSG_AVAILABLE_US') === 'true';
  const router = useLegacyRouter();
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const toggleStoreModal = () => setStoreModalOpen((state) => !state);
  const isStoreSelected = useIsStoreSelected();
  const [storePickUpSelected, setStorePickUpSelected] = useState(
    shippingType === cartConstants.STOREORDER,
  );
  const [homeDeliverySelected, setHomeDeliverySelected] = useState(
    shippingType === cartConstants.ONLINEORDER && fulfillmentId !== FULFILLMENT_METHODS.SAMEDAY,
  );
  const isCartPage = router.route === '/cart';
  const storePickUpButtonRef = useRef<HTMLButtonElement>(null);
  const homeDeliveryButtonRef = useRef<HTMLButtonElement>(null);
  const sameDayDeliveryButtonRef = useRef<HTMLButtonElement>(null);

  const [sameDayDeliverySelected, setSameDayDeliverySelected] = useState(
    shippingType === cartConstants.ONLINEORDER && fulfillmentId === FULFILLMENT_METHODS.SAMEDAY,
  );
  const { data: storeDetails } = useStoreDetailsData();
  const { toggleSisterStoreFlyout: toggleFlyout, isSisterStoreFlyoutOpen } =
    use(SisterStoreContext);

  const toggleSisterStoreFlyout = () => {
    const flyoutData: FlyoutDataState = {
      skuId: skuId ?? '',
      shippingType: shippingType,
      quantityInCart: quantityInCart,
      productDetails: itemData,
    };
    if (productId) {
      flyoutData.productDetails = {
        ...itemData,
        productInfo: {
          productId,
        },
      };
    }
    toggleFlyout(flyoutData);
  };

  const isVdpDsUniqueDisplayEnabled = useForteDecisionFlagWithTrueDefault(
    'enableVdpDsUniqueDisplay',
  );
  const showVdpDsShippingCost = useForteDecisionFlagWithTrueDefault('showVdpDsShippingCost');
  const showVdpDsAsRange = useForteDecisionFlagWithTrueDefault('showVdpDsAsRange');

  /**
   * Focus management effect that moves focus to the appropriate fulfillment option button
   * when the fulfillment method changes. This ensures proper accessibility and user experience
   * by maintaining focus on the newly selected fulfillment option.
   */
  const prevFulfillmentId = usePrevious(fulfillmentId);
  useEffect(() => {
    if (!isCartPage) {
      return;
    }
    if (prevFulfillmentId !== fulfillmentId && prevFulfillmentId) {
      if (fulfillmentId === FULFILLMENT_METHODS.STOREORDER) {
        storePickUpButtonRef.current?.focus();
      }
      if (fulfillmentId === FULFILLMENT_METHODS.ONLINEORDER) {
        homeDeliveryButtonRef.current?.focus();
      }
      if (fulfillmentId === FULFILLMENT_METHODS.SAMEDAY) {
        sameDayDeliveryButtonRef.current?.focus();
      }
    }
  }, [fulfillmentId, prevFulfillmentId, isCartPage]);

  const [firstButton, secondButton, thirdButton] = determineOptionsToDisplayButtons(
    fulfillmentOptionsGroupList,
  );

  const sisterStoreAvailable = (item: FulfillmentGroup) =>
    item.groupFulfillmentOptions?.find((group) => group.groupId === FULFILLMENT_METHODS.STOREORDER)
      ?.sisterStoreAvailable ?? false;

  const handleClickStoreOrder = (item: FulfillmentGroup) => {
    const isSisterStoreAvailable = sisterStoreAvailable(item);
    if (!isStoreSelected) {
      toggleStoreModal();
      return null;
    }
    if (
      (optionChecked(FULFILLMENT_METHODS.STOREORDER) && !isSisterStoreAvailable) ||
      disableRemoveButton
    ) {
      return null;
    }
    if (item.available || (router.pathname === '/products/[...slug]' && isSisterStoreAvailable)) {
      if (!isSisterStoreFlyoutOpen) {
        changeShippingType(
          cartConstants.STOREORDER,
          item.groupId,
          item.maxAvailableQty,
          sisterStoreAvailable(item),
          FULFILLMENT_METHODS.STOREORDER,
          firstButton?.fulfillmentTypeIdOriginal,
        );

        setStorePickUpSelected(true);
        setHomeDeliverySelected(false);
        setSameDayDeliverySelected(false);
      }
    } else if (isSisterStoreAvailable) {
      if (item.groupId === FULFILLMENT_METHODS.STOREORDER && router.pathname === routePaths.cart) {
        toggleSisterStoreFlyout();
      }
    }
  };

  const handleClickHomeDelivery = (item: FulfillmentGroup) => {
    if (
      fulfillmentId === FULFILLMENT_METHODS.NEXTDAY ||
      fulfillmentId === FULFILLMENT_METHODS.ONLINEORDER ||
      disableRemoveButton
    ) {
      return null;
    }
    if (item.available) {
      const fulfillmentId = nddOptionAvailable(item.groupFulfillmentOptions ?? [])
        ? FULFILLMENT_METHODS.NEXTDAY
        : FULFILLMENT_METHODS.ONLINEORDER;

      changeShippingType(
        cartConstants.ONLINEORDER,
        item.groupId,
        fulfillmentId && fulfillmentId === FULFILLMENT_METHODS.NEXTDAY
          ? nddQuantity!
          : item.maxAvailableQty,
        sisterStoreAvailable(item),
        fulfillmentId,
        secondButton?.fulfillmentTypeIdOriginal,
      );

      setStorePickUpSelected(false);
      setHomeDeliverySelected(true);
      setSameDayDeliverySelected(false);
    }
  };

  const handleClickSameDayDelivery = (item: FulfillmentGroup) => {
    if (fulfillmentId === FULFILLMENT_METHODS.SAMEDAY || disableRemoveButton) {
      return null;
    }

    if (item.available) {
      changeShippingType(
        cartConstants.ONLINEORDER,
        item.groupId,
        sddQuantity!,
        sisterStoreAvailable(item),
        FULFILLMENT_METHODS.SAMEDAY,
        thirdButton?.fulfillmentTypeIdOriginal,
      );

      setStorePickUpSelected(false);
      setHomeDeliverySelected(false);
      setSameDayDeliverySelected(true);
    }
  };

  const nddOptionAvailable = (groupFulfillmentOptions: FulfillmentOption[] = []) => {
    return groupFulfillmentOptions.some((option) => {
      if (option.fulfillmentTypeId === FULFILLMENT_METHODS.NEXTDAY && option.available) {
        return isCart ? option.availableQty >= quantityInCart : option.availableQty >= count;
      }
      return false;
    });
  };
  const isNddAvailable = nddOptionAvailable(
    fulfillmentOptionsGroupList.flatMap((group) => group.groupFulfillmentOptions ?? []),
  );

  const sddOptionAvailable = (groupFulfillmentOptions: FulfillmentOption[] = []) => {
    return groupFulfillmentOptions.some((option) => {
      if (option.fulfillmentTypeId === FULFILLMENT_METHODS.SAMEDAY && option.available) {
        return isCart ? option.availableQty >= quantityInCart : option.availableQty >= count;
      }
      return false;
    });
  };

  const standardDeliveryAvailable = (groupFulfillmentOptions: FulfillmentOption[] = []) => {
    return groupFulfillmentOptions.some((option) => {
      if (
        (option.fulfillmentTypeId === FULFILLMENT_METHODS.ONLINEORDER ||
          option.fulfillmentTypeId === FULFILLMENT_METHODS.CUSTOMIZEDSKU) &&
        option.available
      ) {
        return isCart ? option.availableQty >= quantityInCart : option.availableQty >= count;
      }
      return false;
    });
  };

  const getEstimatedDeliveryText = (fulFillmentOptions: FulfillmentOption[]) => {
    return fulFillmentOptions
      .map((option) => {
        if (
          option.fulfillmentTypeId === FULFILLMENT_METHODS.ONLINEORDER ||
          option.fulfillmentTypeId === FULFILLMENT_METHODS.CUSTOMIZEDSKU
        ) {
          return option.estimatedDeliveryDate?.split(':');
        }
      })
      .filter((item) => {
        return item !== undefined;
      });
  };

  const checkIfVdpDsFulfillmentIsAvailable = (
    groupFulfillmentOptions: FulfillmentOption[] = [],
  ) => {
    const isAvailable = groupFulfillmentOptions.some((option) => {
      if (option.fulfillmentTypeIdOriginal === 103 && option.available) {
        return true;
      }
      return false;
    });
    return isAvailable;
  };

  const findVdpDsFulfillmentOption = (groupFulfillmentOptions: FulfillmentOption[] = []) => {
    const fulfillment = groupFulfillmentOptions.find(
      (option) => option.fulfillmentTypeIdOriginal === 103,
    );
    return fulfillment;
  };

  const getHomeDeliveryButtonTopContent = (item: {
    groupLabel: any;
    groupFulfillmentOptions?: FulfillmentOption[];
  }) => {
    const vdpDsIsAvailable = checkIfVdpDsFulfillmentIsAvailable(item.groupFulfillmentOptions);

    if (vdpDsIsAvailable && isVdpDsUniqueDisplayEnabled && !isNddAvailable) {
      const vdpDsType = showVdpDsAsRange ? 'ground' : 'express';

      if (vdpDsType === 'ground') {
        const [, delivery] = labels.label_shipping_ground_delivery.split(' ');
        return delivery;
      }
      return labels.label_shipping_express_delivery;
    }
    return labels.label_home_delivery;
  };
  const getHomeDeliveryButtonSubContent = (
    item: {
      groupLabel: any;
      groupFulfillmentOptions?: FulfillmentOption[];
    },
    hideButton: boolean,
  ) => {
    const prioritizeNddMessage = nddOptionAvailable(item.groupFulfillmentOptions);
    const prioritizeStandardDeliveryMessage =
      !nddOptionAvailable(item.groupFulfillmentOptions) &&
      standardDeliveryAvailable(item.groupFulfillmentOptions);
    const vdpDsFulfillmentIsAvailable = checkIfVdpDsFulfillmentIsAvailable(
      item.groupFulfillmentOptions,
    );

    if (isVdpDsUniqueDisplayEnabled && vdpDsFulfillmentIsAvailable && !isNddAvailable) {
      const vdpDsOption = findVdpDsFulfillmentOption(item.groupFulfillmentOptions);
      const vdpDsType = showVdpDsAsRange ? 'ground' : 'express';
      if (vdpDsType === 'ground') {
        return (
          <span className={styles.bopusAvailableTextContainer}>
            <span className={cx(styles.readyToday)}>
              {`${labels.label_fulfilment_options_get_it} `}
              <span className={cx(styles.successText, styles.capitalize)}>
                {`${createFreeGroundDateRange(vdpDsOption?.estimatedDeliveryDate)} `}
              </span>
            </span>
            <span className={cx(styles.freeTextUppercase, styles.threeSplit)}>
              {labels.label_free}
            </span>
          </span>
        );
      }

      if (vdpDsType === 'express') {
        const { getItPhrase, formattedDate } = generateGetItTimePhrase({
          labels: {
            lblTomorrow: labels.label_tomorrow,
            lblGetIt: labels.label_fulfilment_options_get_it,
            lblGetItBy: labels.label_get_it_by,
          },
          estimatedDeliveryDate: String(vdpDsOption?.estimatedDeliveryDate),
        });

        if (showVdpDsShippingCost) {
          return (
            <>
              <span
                className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
                  [styles.disabledText]: hideButton,
                })}
              >
                {getItPhrase}
                <span className={cx(styles.successText)}>{formattedDate}</span>
              </span>

              <span
                className={cx(styles.freeText, styles.threeSplit, {
                  [styles.disabledText]: hideButton,
                })}
              >
                <span className={cx(styles.freeTextUppercase, styles.threeSplit)}>{'$4.99'} </span>
              </span>
            </>
          );
        } else {
          return (
            <>
              <span
                className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
                  [styles.disabledText]: hideButton,
                })}
              >
                {getItPhrase}
                <span className={cx(styles.successText, styles.deliveryTodayOrTomorrow)}>
                  <p>{formattedDate}</p>
                </span>
              </span>
            </>
          );
        }
      }
    }

    switch (true) {
      case prioritizeNddMessage: {
        const nddEstDeliveryDateString = item.groupFulfillmentOptions?.find(
          (option) => option.fulfillmentTypeId === FULFILLMENT_METHODS.NEXTDAY,
        )?.estimatedDeliveryDate;
        const estDeliveryDateTime = dayjs(nddEstDeliveryDateString);
        return (
          <>
            <span
              className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
                [styles.disabledText]: hideButton,
              })}
            >
              {labels.label_get_it}
              <span className={cx(styles.successText, styles.deliveryTodayOrTomorrow)}>
                {checkIfDateIsTomorrow(nddEstDeliveryDateString ?? '') ? (
                  <p>{labels.label_tomorrow}</p>
                ) : (
                  <p className={styles.deliveryMessageCapitalized}>
                    {estDeliveryDateTime.format(dateFormatterForSTH.weekDayMonthAndDay)}
                  </p>
                )}
              </span>
            </span>

            <span
              className={cx(styles.freeText, styles.threeSplit, {
                [styles.disabledText]: hideButton,
              })}
            >
              <span className={cx(styles.freeTextUppercase, styles.threeSplit)}>
                {labels.label_free}{' '}
              </span>
              <span
                className={cx(styles.nddFree, styles.threeSplit, {
                  [styles.disabledText]: hideButton,
                })}
              >
                {labels.label_fulfillment_options_over_35}
              </span>
            </span>
          </>
        );
      }

      case prioritizeStandardDeliveryMessage: {
        const estDeliveryDate = getEstimatedDeliveryText(item.groupFulfillmentOptions ?? []);
        const hasDateRange =
          estDeliveryDate?.[0]?.[1]?.includes('-') || estDeliveryDate?.[1]?.[1]?.includes('-');
        const estimatedDeliveryDateDisplay =
          estDeliveryDate?.[0]?.[1]?.replace(/\b0/g, '') ||
          estDeliveryDate?.[1]?.[1]?.replace(/\b0/g, '');

        return (
          <>
            <span
              className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
                [styles.disabledText]: hideButton,
                [styles.estDeliveryTextContainerStyles]: hasDateRange,
              })}
            >
              {hasDateRange ? labels.label_get_it : labels.label_get_it_by}
              <span
                className={cx(styles.successText, {
                  [styles.disabledText]: hideButton,
                })}
              >
                <p>
                  {getTomorrowOrDate(estimatedDeliveryDateDisplay ?? '', labels.label_tomorrow)}
                </p>
              </span>
            </span>
          </>
        );
      }
      default:
        return (
          <>
            <span
              className={cx(
                styles.buttonSubtextStyles,
                styles.homeDeliveryUnavailable,
                styles.threeSplit,
                {
                  [styles.disabledText]: hideButton,
                },
              )}
            >
              {labels.label_not_available}
            </span>
          </>
        );
    }
  };

  const optionChecked = (id: number) => {
    return fulfillmentId === id;
  };

  const getSameDayDeliveryButtonSubContent = (
    item: {
      groupFulfillmentOptions?: FulfillmentOption[];
    },
    disabledButton: boolean,
  ) => {
    const startingAt = labels.label_sdd_shipping_price.match(/starting at/i)?.[0] ?? '';
    const price = labels.label_sdd_shipping_price.substring(startingAt.length);
    return sddOptionAvailable(item.groupFulfillmentOptions) ? (
      <>
        <span
          className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
            [styles.disabledText]: disabledButton,
          })}
        >
          {labels.label_get_it}
          <span
            className={cx(styles.deliveryTodayOrTomorrow, {
              [styles.disabledText]: disabledButton,
            })}
          >
            <p>{labels.label_today}</p>
          </span>
        </span>
        <span className={cx(styles.nddFree, styles.threeSplit)}>
          {startingAt}
          <span className={styles.freeText}>{price}</span>
        </span>
      </>
    ) : (
      <>
        <span
          className={cx(
            styles.buttonSubtextStyles,
            styles.homeDeliveryUnavailable,
            styles.threeSplit,
            {
              [styles.disabledText]: disabledButton,
            },
          )}
        >
          {labels.label_not_available}
        </span>
      </>
    );
  };

  const getStorePickupButtonSubContent = (
    item: { available: boolean; groupFulfillmentOptions?: FulfillmentOption[] },
    disableButton: boolean,
  ) => {
    const availableInSisterStores =
      !item.available &&
      Array.isArray(item.groupFulfillmentOptions) &&
      item.groupFulfillmentOptions[0]?.sisterStoreAvailable;
    const availableinSetStore = item.available;
    const notAvailable = !item.available && Array.isArray(item.groupFulfillmentOptions);
    const notAvailableatSisterStores =
      !item.available &&
      Array.isArray(item.groupFulfillmentOptions) &&
      !item.groupFulfillmentOptions[0]?.sisterStoreAvailable;
    switch (true) {
      case !isStoreSelected:
        return (
          <Text size="062" color="600" className={styles.selectStoreStyles}>
            <Text
              decoration="underline"
              className={styles.selectStoreStyles}
              attributes={{ role: 'button' }}
            >
              <span>{labels.label_fulfillment_select_store}</span>
            </Text>
            <span> {labels.label_fulfillment_availability} </span>
          </Text>
        );
      case availableinSetStore:
        return (
          <span className={styles.bopusAvailableTextContainer}>
            <span
              className={cx(styles.readyToday, {
                [styles.disabledText]: disableButton,
              })}
            >
              {labels.label_ready}
              <span className={cx(styles.successText, styles.storeTimeMargin)}>
                <StorePickupTime storeDetails={storeDetails} />
              </span>
            </span>
            <span
              className={cx(styles.freeTextUppercase, styles.threeSplit, {
                [styles.disabledText]: disableButton,
              })}
            >
              {labels.label_free}
            </span>
          </span>
        );
      case availableInSisterStores:
        return (
          <>
            <span
              className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
                [styles.disabledText]: disableButton,
              })}
            >
              {`${
                item.groupFulfillmentOptions?.[0]?.numberOfSisterStoreAvailable !== -1
                  ? `${item.groupFulfillmentOptions?.[0]?.numberOfSisterStoreAvailable} `
                  : ''
              }nearby store${
                item.groupFulfillmentOptions?.[0]?.numberOfSisterStoreAvailable === -1 ||
                (item.groupFulfillmentOptions?.[0]?.numberOfSisterStoreAvailable ?? 0) > 1
                  ? 's'
                  : ''
              }`}
            </span>
            <span
              className={cx(styles.freeTextUppercase, styles.threeSplit, {
                [styles.disabledText]: disableButton,
              })}
            >
              {labels.label_free}
            </span>
          </>
        );
      case notAvailable:
        return (
          <span
            className={cx(styles.buttonSubtextStyles, styles.threeSplit, {
              [styles.disabledText]: disableButton,
            })}
          >
            {notAvailableatSisterStores
              ? labels.label_not_available_near_you
              : labels.label_not_available}
          </span>
        );
    }
  };

  useEffect(() => {
    const homeDeliveryButton = homeDeliveryButtonRef.current;
    if (platform !== 'ios' || !homeDeliveryButton) {
      return;
    }

    // trigger a reflow whenever the count updates to avoid button content glitching
    homeDeliveryButton.style.display = 'inline-flex';
    const timeoutId = setTimeout(() => {
      homeDeliveryButton.style.display = '';
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [count, platform]);

  return (
    <div className={styles.fulfilmentGroup}>
      <div className={styles.fulfillmentOptionCardsContainer}>
        {fulfillmentOptionsGroupList.map((item) => {
          const isOptionCheckedOnCurrentPage = (id: number) => {
            return isCartPage ? fulfillmentId === id && item.selected : fulfillmentId === id;
          };
          const sisterStoreAvailable =
            Array.isArray(item.groupFulfillmentOptions) &&
            item.groupFulfillmentOptions[0]?.sisterStoreAvailable;

          const disabled =
            item.groupId === FULFILLMENT_METHODS.ONLINEORDER
              ? (!nddOptionAvailable(item.groupFulfillmentOptions ?? []) &&
                  !standardDeliveryAvailable(item.groupFulfillmentOptions ?? [])) ||
                !item.available
              : !sisterStoreAvailable && !item.available;
          let disableButton = isBopusNewMsgApplicable
            ? disabled && (item.groupId !== FULFILLMENT_METHODS.STOREORDER || isStoreSelected)
            : !item.available &&
              (item.groupId !== FULFILLMENT_METHODS.STOREORDER || isStoreSelected);
          let disabledSameDayButton = isBopusNewMsgApplicable
            ? !sddOptionAvailable(item.groupFulfillmentOptions ?? [])
            : true;
          if (item.groupId === FULFILLMENT_METHODS.ONLINEORDER) {
            return (
              <Fragment key={`${item.groupId}-btn`}>
                <Button
                  aria-pressed={
                    isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.NEXTDAY) ||
                    isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.ONLINEORDER)
                  }
                  data-testid="home-delivery-fulfillment-btn"
                  customClass={cx(styles.v2ButtonStyles, styles.buttonCommonStyles, {
                    [styles.activeButtonStyles]:
                      (isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.NEXTDAY) ||
                        isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.ONLINEORDER) ||
                        isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.CUSTOMIZEDSKU) ||
                        (homeDeliverySelected &&
                          shippingType === cartConstants.ONLINEORDER &&
                          !isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.SAMEDAY))) &&
                      !disableButton,
                    [styles.disabledStyles]: disableButton,
                    [styles.v2ButtonShadow]: !disableButton,
                  })}
                  disabled={disableButton}
                  type="button"
                  onClick={() => handleClickHomeDelivery(item)}
                  reference={homeDeliveryButtonRef}
                >
                  <span
                    className={cx(styles.sthCard, {
                      [styles.disabledText]: disableButton,
                    })}
                  >
                    {getHomeDeliveryButtonTopContent(item)}
                  </span>
                  {getHomeDeliveryButtonSubContent(item, disableButton)}
                </Button>

                <Button
                  aria-pressed={isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.SAMEDAY)}
                  data-testid="same-day-delivery-fulfillment-btn"
                  customClass={cx(styles.v2ButtonStyles, styles.buttonCommonStyles, {
                    [styles.activeButtonStyles]:
                      (isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.SAMEDAY) &&
                        sddOptionAvailable(item.groupFulfillmentOptions ?? [])) ??
                      (sameDayDeliverySelected && !disabledSameDayButton),
                    [styles.disabledStyles]: disabledSameDayButton,
                    [styles.v2ButtonShadow]: !disabledSameDayButton,
                  })}
                  disabled={disabledSameDayButton}
                  type="button"
                  onClick={() => handleClickSameDayDelivery(item)}
                  reference={sameDayDeliveryButtonRef}
                >
                  <span
                    className={cx(styles.sthCard, {
                      [styles.disabledText]: disabledSameDayButton,
                    })}
                  >
                    {labels.label_same_Day}
                  </span>
                  {getSameDayDeliveryButtonSubContent(item, disabledSameDayButton)}
                </Button>
              </Fragment>
            );
          }

          if (item.groupId === FULFILLMENT_METHODS.STOREORDER) {
            const isButtonSelected = isCart
              ? isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.STOREORDER) &&
                !sisterStoreAvailable
              : isOptionCheckedOnCurrentPage(FULFILLMENT_METHODS.STOREORDER);
            return (
              <Button
                key={`${item.groupId}-btn`}
                aria-pressed={isButtonSelected}
                data-testid="store-pickup-fulfillment-btn"
                customClass={cx(styles.v2ButtonStyles, styles.buttonCommonStyles, {
                  [styles.activeButtonStyles]:
                    (isButtonSelected ?? storePickUpSelected) &&
                    (!disableButton ||
                      (shippingType === cartConstants.STOREORDER &&
                        item?.groupFulfillmentOptions?.[0]?.sisterStoreAvailable)),
                  [styles.disabledStyles]: disableButton,
                  [styles.v2ButtonShadow]: !disableButton,
                })}
                disabled={disableButton}
                type="button"
                onClick={() => handleClickStoreOrder(item)}
                reference={storePickUpButtonRef}
              >
                <span className={cx(styles.bopusCard, { [styles.disabledText]: disableButton })}>
                  {labels.label_instore_pickup}
                </span>
                {getStorePickupButtonSubContent(item, disableButton)}
              </Button>
            );
          }
        })}
      </div>

      {fulfillmentOptionsGroupList.map((item, buttonIndex) => {
        const defaultCheckedGroup =
          shippingType === cartConstants.STOREORDER
            ? FULFILLMENT_METHODS.STOREORDER
            : FULFILLMENT_METHODS.ONLINEORDER;
        return (
          item.groupFulfillmentOptions &&
          item.groupId === defaultCheckedGroup && (
            <div
              className={styles.subgroupContainer}
              key={`${fulfillmentId}-subgroup`}
              data-testid={`${fulfillmentId}-subgroup`}
            >
              <SubGroupText
                fulfillmentOptionsGroupList={fulfillmentOptionsGroupList}
                itemData={itemData}
                isDealDrawer={isDealDrawer}
                skuId={skuId}
                productName={productName}
                groupId={item.groupId}
                handleInstockAnalytics={handleInstockAnalytics}
                ref={subGroupRefs[buttonIndex].ref}
                itemPresentInCart={itemPresentInCart}
                toggleSisterStoreFlyout={toggleSisterStoreFlyout}
                nddOptionAvailable={nddOptionAvailable(
                  fulfillmentOptionsGroupList.flatMap(
                    (group) => group.groupFulfillmentOptions ?? [],
                  ),
                )}
                nextDayDeliverySelected={optionChecked(FULFILLMENT_METHODS.NEXTDAY)}
                sameDayDeliverySelected={optionChecked(FULFILLMENT_METHODS.SAMEDAY)}
                storePickUpSelected={optionChecked(FULFILLMENT_METHODS.STOREORDER)}
              />
            </div>
          )
        );
      })}
      {storeModalOpen && !isStoreSelected && (
        <SearchStoreModal closeStoreModal={toggleStoreModal} />
      )}
    </div>
  );
}
